import axios from 'axios';
import { Keys } from '/src/config.js';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

axios.defaults.baseURL = Keys.VUE_APP_API_URL + '/api';

// Request interceptor: always read fresh token from localStorage before each request
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('freshToken');
    if (token && token !== 'null') {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Gjuha lexohet ne cdo kerkese, jo nje here ne nisje, qe ndryshimi ta
    // zere efektin menjehere. Serveri e perdor vetem per te dhenat e demos,
    // te cilat i perkthen ne dalje; per llogarite reale nuk ka asnje efekt.
    config.headers['X-Locale'] =
      localStorage.getItem('locale') === 'en' ? 'en' : 'sq';

    return config;
  },
  (error) => Promise.reject(error),
);

// Queue of requests that failed while a token refresh was in progress
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

const clearSessionAndRedirect = () => {
  localStorage.setItem('freshToken', null);
  localStorage.setItem('userRole', null);
  localStorage.setItem('simple_mode', null);
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Wait for Firebase to restore auth state from persistence (fixes race condition on page reload)
const getFirebaseUser = () =>
  new Promise((resolve) => {
    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });

// Response interceptor: on 401, try to silently refresh via Firebase, else redirect to login
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Demoja ka mbaruar kohen e lire: serveri e refuzon kerkesen derisa
    // vizitori te lere te dhenat dhe te verifikoje email-in. DemoBanner
    // e degjon kete dhe hap dritaren.
    if (error.response?.status === 403 && error.response.data?.demo_lead_required) {
      window.dispatchEvent(new CustomEvent('demo-lead-required'));
      // Kerkesa mbetet pezull, jo e refuzuar: perndryshe cdo pamje do te
      // nxirrte gabimin e vet mbi dritaren. Pas verifikimit faqja rifreskohet.
      return new Promise(() => {});
    }

    // Only handle 401 and avoid infinite retry loops
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // If a refresh is already in progress, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axios(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Wait for Firebase to finish restoring session (important after page reload)
      const firebaseUser = await getFirebaseUser();

      if (!firebaseUser) {
        // No Firebase session — user must log in again
        clearSessionAndRedirect();
        return Promise.reject(error);
      }

      // Force-refresh the Firebase ID token (Firebase handles its own expiry)
      const idToken = await firebaseUser.getIdToken(true);
      const deviceName = (navigator.userAgent || 'web').substring(0, 100);

      // Exchange Firebase token for a new Sanctum token
      const { data } = await axios.post(
        '/auth/loginViaToken',
        { device_name: deviceName, token: idToken },
        { _retry: true },
      );

      const freshToken = data.token.split('|')[1];
      localStorage.setItem('freshToken', freshToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${freshToken}`;

      // Retry all queued requests with the new token
      processQueue(null, freshToken);

      // Retry the original failed request
      originalRequest.headers['Authorization'] = `Bearer ${freshToken}`;
      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearSessionAndRedirect();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default axios;
